/**
 * Environment configuration interface
 */
export interface AuthPublicConfig {
  productionAppUrl: string
  productionHostname: string
  helpEmail: string
  customerServicePhone: string
  notificationsEmail: string
  chromeDownloadUrl: string
}

export interface EnvConfig {
  apiBaseUrl: string
  apiTimeout: number
  appName: string
  appStoreUrl: string
  playStoreUrl: string
  googleApiKey?: string
  environment: 'development' | 'staging' | 'production'
  auth: AuthPublicConfig
}

/**
 * API Base URLs for different environments
 */
const API_URLS: Record<'development' | 'staging' | 'production', string> = {
  development: 'https://api.dev.libertyrent.com',
  staging: 'https://api.cornerstage.libertyrent.com',
  production: 'https://api.cornerstone.libertyrent.com',
} as const

/**
 * Valid environment types
 */
type Environment = 'development' | 'staging' | 'production'

/**
 * Get environment configuration from environment variables
 * @returns Environment configuration object
 */
const getEnvConfig = (): EnvConfig => {
  const envMode = (
    import.meta.env.VITE_ENVIRONMENT ||
    import.meta.env.MODE ||
    'staging'
  ) as Environment

  const apiBaseUrl =
    import.meta.env.VITE_API_BASE_URL ||
    API_URLS[envMode] ||
    API_URLS.staging

  const apiTimeout = parseInt(import.meta.env.VITE_API_TIMEOUT || '30000', 10)

  return {
    apiBaseUrl,
    apiTimeout,
    appName: import.meta.env.VITE_APP_NAME || 'Liberty Rent',
    appStoreUrl:
      import.meta.env.VITE_APP_STORE_URL ||
      'https://apps.apple.com/us/app/liberty-rent/id1579491456',
    playStoreUrl:
      import.meta.env.VITE_PLAY_STORE_URL ||
      'https://play.google.com/store/apps/details?id=com.liberty.libertyrent',
    googleApiKey: import.meta.env.VITE_GOOGLE_API_KEY || 'AIzaSyA5YLhaDy4fTWATHnjZOm-auIBsfjyiR0w',
    environment: envMode,
    auth: {
      productionAppUrl:
        import.meta.env.VITE_PRODUCTION_APP_URL || 'https://cornerstone.libertyrent.com',
      productionHostname:
        import.meta.env.VITE_PRODUCTION_HOSTNAME || 'cornerstone.libertyrent.com',
      helpEmail: import.meta.env.VITE_HELP_EMAIL || 'help@libertyrent.com',
      customerServicePhone:
        import.meta.env.VITE_CUSTOMER_SERVICE_PHONE || '(877) 581-7368',
      notificationsEmail:
        import.meta.env.VITE_NOTIFICATIONS_EMAIL || 'notifications@libertyrent.io',
      chromeDownloadUrl:
        import.meta.env.VITE_CHROME_DOWNLOAD_URL || 'https://www.google.com/chrome/',
    },
  }
}

/**
 * Environment configuration
 * Automatically loads from environment variables
 */
export const env = getEnvConfig()

/**
 * Base URL for URLs built in the browser (XHR, img src, window.open) so they match the axios client routing:
 * - In Vite dev, use same-origin relative paths so the dev server proxy forwards to the API (avoids CORS and
 *   ensures session `token` headers work the same as axios).
 * - In production, use the configured API origin.
 */
export function getBrowserApiBaseUrl(): string {
  if (import.meta.env.DEV) return ''
  return env.apiBaseUrl
}

/** True when the current site is not the production Liberty Rent hostname (sandbox banner parity). */
export function isSandboxEnvironment(): boolean {
  if (typeof window !== 'undefined') {
    return window.location.hostname !== env.auth.productionHostname
  }
  return env.environment !== 'production'
}
